import React from 'react';
import {
    Box,
    Container,
    Typography,
    Paper,
    Button,
    TextField,
    InputAdornment,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
    IconButton,
    Grid,
    Autocomplete,
    Collapse,
    Pagination,
    Stack,
    Tooltip
} from '@mui/material';
import {
    Search,
    Add,
    Save,
    Close,
    Group,
    Visibility,
    VisibilityOff,
    PersonAdd
} from '@mui/icons-material';
import BorderColorIcon from '@mui/icons-material/BorderColor';
import UserManagementVM from "../viewmodel/UserManagementVM";
import PermissionView from "./PermissionView";
import DataManagementView from "./DataManagementView";
import { UserKeys } from "../../assets/VarConstants";
import CBackDrop from "../../../basecomp/components/CBackDrop";

const UserManagementView = () => {
    const {
        currentUsers,
        userTypes,
        userTerminals,
        filteredUsers,
        activeTab,
        searchTerm,
        showAddForm,
        editingUser,
        newUser,
        paginationInfo,
        totalPages,
        jumpToPage,
        inlineEditingUser,
        inlineEditData,
        handleSearchChange,
        handleShowAddForm,
        handlePageChange,
        handleQuickJump,
        handleJumpInputKeyPress,
        handleJumpToPageChange,
        handleStartInlineEdit,
        handleCancelInlineEdit,
        handleSaveInlineEdit,
        handleInlineDataChange,
        handleAddUser,
        handleUpdateUser,
        handleCancelForm,
        handleNewUserChange,
        isLoading
    } = UserManagementVM();

    // Get permissions from localStorage
    const permissionData = localStorage.getItem("permissionData") || "";
    const permissions = permissionData.split(",");

    // Check specific permissions
    const canAddUser = permissions.includes("feature_user_add_user");
    const canUpdateUser = permissions.includes("feature_user_update_user");

    // console.log(
    //     "DB value for USER_STATUS:",
    //     inlineEditData[UserKeys.USER_STATUS]
    // );

    const selectedStatus =
        [{ id: 1, name: 'Active' }, { id: 0, name: 'Inactive' }]
            .find(status => status.id === Number(inlineEditData[UserKeys.USER_STATUS])) || null;

    // console.log('Selected status:', selectedStatus);

    const renderUsersList = () => (
        <Paper
            elevation={4}
            sx={{
                borderRadius: '16px',
                overflow: 'hidden',
                border: '1px solid rgba(60, 176, 67, 0.2)',
                marginTop: "1.5em",
                boxShadow: '0 10px 30px rgba(25, 25, 112, 0.1)'
            }}
        >
            {/* Header */}
            <Box
                sx={{
                    background: '#3CB043',
                    p: { xs: 2, sm: 3, md: 3.5 },
                    color: 'white',
                    position: 'relative',
                    overflow: 'hidden',
                    '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'linear-gradient(45deg, rgba(255,255,255,0.1) 0%, transparent 50%)',
                        pointerEvents: 'none'
                    }
                }}
            >
                <Grid container spacing={2} alignItems="center" sx={{ position: 'relative', zIndex: 1 }}>
                    <Grid item xs={12} lg={8}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                            <Group sx={{ fontSize: 28, color: 'white' }} />
                            <Typography variant="body1" sx={{
                                color: 'rgba(255, 255, 255, 0.95)',
                                fontWeight: 900,
                                fontSize: { xs: '0.9rem', sm: '1rem' },
                                textShadow: '0 1px 2px rgba(0,0,0,0.2)',
                                flexGrow: 1
                            }}>
                                User Details
                            </Typography>
                        </Box>
                    </Grid>
                    <Grid item xs={12} lg={4}>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 2,
                                justifyContent: { xs: 'flex-start', lg: 'flex-end' }
                            }}
                        >
                            <TextField
                                size="small"
                                placeholder="Search users..."
                                value={searchTerm}
                                onChange={(e) => handleSearchChange(e.target.value)}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Search sx={{ color: '#3CB043' }} />
                                        </InputAdornment>
                                    ),
                                    endAdornment:
                                        searchTerm && (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleSearchChange("")}
                                                    edge="end"
                                                    sx={{ color: '#ef4444' }}
                                                >
                                                    <Close />
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                }}
                                sx={{
                                    backgroundColor: 'white',
                                    borderRadius: '3px',
                                    minWidth: { xs: '200px', sm: '250px', md: '280px' },
                                    '& .MuiOutlinedInput-root': {
                                        height: '44px',
                                        '& fieldset': {
                                            borderColor: 'transparent',
                                            borderWidth: '2px'
                                        },
                                        '&:hover fieldset': {
                                            borderColor: '#3CB043',
                                            borderWidth: '2px'
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: '#3CB043',
                                            borderWidth: '2px'
                                        },
                                    },
                                }}
                            />
                            {canAddUser && (
                                <Button
                                    variant="contained"
                                    onClick={handleShowAddForm}
                                    sx={{
                                        backgroundColor: '#042560',
                                        color: 'white',
                                        border: '2px solid rgba(255,255,255,0.2)',
                                        boxShadow: '0 4px 12px rgba(4, 37, 96, 0.3)',
                                        '&:hover': {
                                            backgroundColor: '#031e4d',
                                            boxShadow: '0 6px 16px rgba(4, 37, 96, 0.4)',
                                            transform: 'translateY(-1px)'
                                        },
                                        textTransform: 'none',
                                        fontWeight: 600,
                                        borderRadius: '3px',
                                        height: '44px',
                                        px: 1.5,
                                        fontSize: '0.95rem',
                                        transition: 'all 0.2s ease',
                                        minWidth: '44px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}
                                >
                                    <PersonAdd />
                                </Button>
                            )}
                        </Box>
                    </Grid>
                </Grid>
            </Box>

            {/* Add/Edit User Form */}
            <Collapse in={showAddForm || editingUser}>
                <Box sx={{
                    p: { xs: 2, sm: 3, md: 4 },
                    backgroundColor: 'linear-gradient(135deg, #f0fdf4 0%, #f0f9ff 100%)',
                    borderBottom: '2px solid rgba(60, 176, 67, 0.1)',
                    position: 'relative'
                }}>
                    <Typography variant="h6" sx={{
                        fontWeight: 700,
                        color: '#042560',
                        mb: 3,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        fontSize: { xs: '1.1rem', sm: '1.25rem' }
                    }}>
                        {editingUser ? <BorderColorIcon /> : <Add />}
                        {editingUser ? 'Edit User' : 'Add New User'}
                    </Typography>

                    <Grid container spacing={{ xs: 2, sm: 3 }} sx={{ mb: 3 }}>
                        <Grid item xs={12} sm={6} lg={3}>
                            <TextField
                                fullWidth
                                type="email"
                                label="Email"
                                placeholder="Enter email"
                                value={newUser[UserKeys.USER_EMAIL]}
                                onChange={(e) => handleNewUserChange(UserKeys.USER_EMAIL, e.target.value)}
                                required
                                InputProps={{
                                    endAdornment: newUser[UserKeys.USER_EMAIL] && (
                                        <InputAdornment position="end">
                                            <IconButton
                                                size="small"
                                                onClick={() => handleNewUserChange(UserKeys.USER_EMAIL, "")}
                                                sx={{ color: '#ef4444' }}
                                            >
                                                <Close fontSize="small" />
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        height: 52,
                                        fontSize: '0.95rem',
                                        backgroundColor: 'white',
                                        borderRadius: '3px',
                                        '& fieldset': {
                                            borderColor: 'rgba(4, 37, 96, 0.2)',
                                            borderWidth: '2px'
                                        },
                                        '&:hover fieldset': {
                                            borderColor: '#3CB043',
                                            borderWidth: '2px'
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: '#042560',
                                            borderWidth: '2px'
                                        },
                                    },
                                    '& .MuiInputLabel-root': {
                                        fontSize: '0.95rem',
                                        color: '#042560',
                                        fontWeight: 500,
                                        '&.Mui-focused': {
                                            color: '#042560',
                                            fontWeight: 600
                                        },
                                    },
                                }}
                            />
                        </Grid>

                        <Grid item xs={12} sm={6} lg={2.5}>
                            <TextField
                                fullWidth
                                label="User Name"
                                placeholder="Enter user name"
                                value={newUser[UserKeys.USER_NAME]}
                                onChange={(e) => handleNewUserChange(UserKeys.USER_NAME, e.target.value)}
                                required
                                InputProps={{
                                    endAdornment: newUser[UserKeys.USER_NAME] && (
                                        <InputAdornment position="end">
                                            <IconButton
                                                size="small"
                                                onClick={() => handleNewUserChange(UserKeys.USER_NAME, "")}
                                                sx={{ color: '#ef4444' }}
                                            >
                                                <Close fontSize="small" />
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        height: 52,
                                        fontSize: '0.95rem',
                                        backgroundColor: 'white',
                                        borderRadius: '3px',
                                        '& fieldset': {
                                            borderColor: 'rgba(4, 37, 96, 0.2)',
                                            borderWidth: '2px'
                                        },
                                        '&:hover fieldset': {
                                            borderColor: '#3CB043',
                                            borderWidth: '2px'
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: '#042560',
                                            borderWidth: '2px'
                                        },
                                    },
                                    '& .MuiInputLabel-root': {
                                        fontSize: '0.95rem',
                                        color: '#042560',
                                        fontWeight: 500,
                                        '&.Mui-focused': {
                                            color: '#042560',
                                            fontWeight: 600
                                        },
                                    },
                                }}
                            />
                        </Grid>

                        <Grid item xs={12} sm={6} lg={2.5}>
                            <Autocomplete
                                options={userTypes}
                                getOptionLabel={(option) => option.user_role_name || option.role || ""}
                                value={
                                    userTypes.find(
                                        type =>
                                            type.user_role_id === newUser[UserKeys.USER_ROLE_ID] ||
                                            type.id === newUser[UserKeys.USER_ROLE_ID]
                                    ) || null
                                }
                                onChange={(event, newValue) => {
                                    handleNewUserChange(
                                        UserKeys.USER_ROLE_ID,
                                        newValue ? (newValue.user_role_id || newValue.id) : ""
                                    );
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="User Type"
                                        placeholder="Select user type"
                                        required
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                height: 52,
                                                fontSize: '0.95rem',
                                                backgroundColor: 'white',
                                                borderRadius: '3px',
                                                '& fieldset': {
                                                    borderColor: 'rgba(4, 37, 96, 0.2)',
                                                    borderWidth: '2px'
                                                },
                                                '&:hover fieldset': {
                                                    borderColor: '#3CB043',
                                                    borderWidth: '2px'
                                                },
                                                '&.Mui-focused fieldset': {
                                                    borderColor: '#042560',
                                                    borderWidth: '2px'
                                                },
                                            },
                                            '& .MuiInputLabel-root': {
                                                fontSize: '0.95rem',
                                                color: '#042560',
                                                fontWeight: 500,
                                                '&.Mui-focused': {
                                                    color: '#042560',
                                                    fontWeight: 600
                                                },
                                            },
                                        }}
                                    />
                                )}
                            />
                        </Grid>

                        <Grid item xs={12} sm={6} lg={2}>
                            <Autocomplete
                                options={userTerminals}
                                getOptionLabel={(option) => option.terminal_name || option.name || ""}
                                value={userTerminals.find(terminal => terminal.terminal_id === newUser[UserKeys.TERMINAL_ID] || terminal.id === newUser[UserKeys.TERMINAL_ID]) || null}
                                onChange={(event, newValue) => {
                                    handleNewUserChange(UserKeys.TERMINAL_ID, newValue ? (newValue.terminal_id || newValue.id) : "");
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Terminal"
                                        placeholder="Select terminal"
                                        required
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                height: 52,
                                                fontSize: '0.95rem',
                                                backgroundColor: 'white',
                                                borderRadius: '3px',
                                                '& fieldset': {
                                                    borderColor: 'rgba(4, 37, 96, 0.2)',
                                                    borderWidth: '2px'
                                                },
                                                '&:hover fieldset': {
                                                    borderColor: '#3CB043',
                                                    borderWidth: '2px'
                                                },
                                                '&.Mui-focused fieldset': {
                                                    borderColor: '#042560',
                                                    borderWidth: '2px'
                                                },
                                            },
                                            '& .MuiInputLabel-root': {
                                                fontSize: '0.95rem',
                                                color: '#042560',
                                                fontWeight: 500,
                                                '&.Mui-focused': {
                                                    color: '#042560',
                                                    fontWeight: 600
                                                },
                                            },
                                        }}
                                    />
                                )}
                            />
                        </Grid>

                        <Grid item xs={12} sm={6} lg={2}>
                            <Autocomplete
                                options={[
                                    { id: '1', name: 'Active' },
                                    { id: '0', name: 'Inactive' }
                                ]}
                                getOptionLabel={(option) => option.name || ""}
                                value={
                                    [{ id: '1', name: 'Active' }, { id: '0', name: 'Inactive' }]
                                        .find(status => status.id === String(newUser[UserKeys.USER_STATUS])) || null
                                }
                                onChange={(event, newValue) => {
                                    handleNewUserChange(UserKeys.USER_STATUS, newValue ? newValue.id : "");
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Status"
                                        placeholder="Select status"
                                        required
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                height: 52,
                                                fontSize: '0.95rem',
                                                backgroundColor: 'white',
                                                borderRadius: '3px',
                                                '& fieldset': {
                                                    borderColor: 'rgba(4, 37, 96, 0.2)',
                                                    borderWidth: '2px'
                                                },
                                                '&:hover fieldset': {
                                                    borderColor: '#3CB043',
                                                    borderWidth: '2px'
                                                },
                                                '&.Mui-focused fieldset': {
                                                    borderColor: '#042560',
                                                    borderWidth: '2px'
                                                },
                                            },
                                            '& .MuiInputLabel-root': {
                                                fontSize: '0.95rem',
                                                color: '#042560',
                                                fontWeight: 500,
                                                '&.Mui-focused': {
                                                    color: '#042560',
                                                    fontWeight: 600
                                                },
                                            },
                                        }}
                                    />
                                )}
                            />
                        </Grid>
                    </Grid>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                        <Button
                            variant="contained"
                            startIcon={<Save />}
                            onClick={editingUser ? handleUpdateUser : handleAddUser}
                            sx={{
                                backgroundColor: '#042560',
                                color: 'white',
                                boxShadow: '0 4px 12px rgba(4, 37, 96, 0.3)',
                                '&:hover': {
                                    backgroundColor: '#031e4d',
                                    boxShadow: '0 6px 16px rgba(4, 37, 96, 0.4)',
                                    transform: 'translateY(-1px)'
                                },
                                textTransform: 'none',
                                fontWeight: 600,
                                borderRadius: '3px',
                                px: 3,
                                py: 1.2,
                                transition: 'all 0.2s ease'
                            }}
                        >
                            {editingUser ? 'Update User' : 'Save User'}
                        </Button>
                        <Button
                            variant="outlined"
                            startIcon={<Close />}
                            onClick={handleCancelForm}
                            sx={{
                                color: '#042560',
                                borderColor: '#042560',
                                borderWidth: '2px',
                                '&:hover': {
                                    backgroundColor: 'rgba(4, 37, 96, 0.1)',
                                    borderColor: '#042560',
                                    borderWidth: '2px'
                                },
                                textTransform: 'none',
                                fontWeight: 600,
                                borderRadius: '3px',
                                px: 3,
                                py: 1.2
                            }}
                        >
                            Cancel
                        </Button>
                    </Stack>
                </Box>
            </Collapse>

            {/* Enhanced Table with Proper Column Width Management */}
            <Box sx={{ width: '100%', overflow: 'hidden' }}>
                <TableContainer sx={{
                    backgroundColor: 'white',
                    overflowX: 'auto',
                    overflowY: 'visible',
                    '&::-webkit-scrollbar': {
                        height: '8px',
                    },
                    '&::-webkit-scrollbar-track': {
                        backgroundColor: '#f1f5f9',
                        borderRadius: '4px',
                    },
                    '&::-webkit-scrollbar-thumb': {
                        backgroundColor: '#cbd5e1',
                        borderRadius: '4px',
                        '&:hover': {
                            backgroundColor: '#94a3b8',
                        },
                    },
                }}>
                    <Table sx={{
                        minWidth: 1200,
                        width: '100%',
                        tableLayout: 'fixed' // This ensures consistent column widths
                    }}>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{
                                    fontWeight: 700,
                                    color: '#042560',
                                    borderBottom: '2px solid rgba(60, 176, 67, 0.2)',
                                    py: 3,
                                    width: '18%', // Increased for name
                                    fontSize: { xs: '0.9rem', sm: '1rem', md: '1.1rem' },
                                    backgroundColor: 'rgba(248, 250, 252, 0.95)'
                                }}>
                                    Name
                                </TableCell>
                                <TableCell sx={{
                                    fontWeight: 700,
                                    color: '#042560',
                                    borderBottom: '2px solid rgba(60, 176, 67, 0.2)',
                                    py: 3,
                                    width: '28%', // Increased for email
                                    fontSize: { xs: '0.9rem', sm: '1rem', md: '1.1rem' },
                                    backgroundColor: 'rgba(248, 250, 252, 0.95)'
                                }}>
                                    Email
                                </TableCell>
                                <TableCell sx={{
                                    fontWeight: 700,
                                    color: '#042560',
                                    borderBottom: '2px solid rgba(60, 176, 67, 0.2)',
                                    py: 3,
                                    width: '20%',
                                    fontSize: { xs: '0.9rem', sm: '1rem', md: '1.1rem' },
                                    backgroundColor: 'rgba(248, 250, 252, 0.95)'
                                }}>
                                    Role
                                </TableCell>
                                <TableCell sx={{
                                    fontWeight: 700,
                                    color: '#042560',
                                    borderBottom: '2px solid rgba(60, 176, 67, 0.2)',
                                    py: 3,
                                    width: '10%',
                                    fontSize: { xs: '0.9rem', sm: '1rem', md: '1.1rem' },
                                    backgroundColor: 'rgba(248, 250, 252, 0.95)'
                                }}>
                                    Terminal
                                </TableCell>
                                <TableCell sx={{
                                    fontWeight: 700,
                                    color: '#042560',
                                    borderBottom: '2px solid rgba(60, 176, 67, 0.2)',
                                    py: 3,
                                    width: '10%',
                                    textAlign: 'center',
                                    fontSize: { xs: '0.9rem', sm: '1rem', md: '1.1rem' },
                                    backgroundColor: 'rgba(248, 250, 252, 0.95)'
                                }}>
                                    Status
                                </TableCell>
                                {canUpdateUser && (
                                    <TableCell sx={{
                                        fontWeight: 700,
                                        color: '#042560',
                                        borderBottom: '2px solid rgba(60, 176, 67, 0.2)',
                                        py: 3,
                                        width: '6%',
                                        textAlign: 'center',
                                        fontSize: { xs: '0.9rem', sm: '1rem', md: '1.1rem' },
                                        backgroundColor: 'rgba(248, 250, 252, 0.95)'
                                    }}>
                                        Actions
                                    </TableCell>
                                )}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {currentUsers.map((user, index) => {
                                const isInlineEditing = inlineEditingUser === user.user_id;

                                return (
                                    <TableRow
                                        key={user.user_id}
                                        sx={{
                                            '&:hover': {
                                                backgroundColor: isInlineEditing ? 'rgba(60, 176, 67, 0.1)' : 'rgba(60, 176, 67, 0.05)',
                                                transform: 'scale(1.001)',
                                                transition: 'all 0.2s ease'
                                            },
                                            borderBottom: '1px solid rgba(60, 176, 67, 0.1)',
                                            backgroundColor: isInlineEditing
                                                ? 'rgba(60, 176, 67, 0.08)'
                                                : index % 2 === 0 ? 'white' : 'rgba(240, 253, 244, 0.3)'
                                        }}
                                    >
                                        {/* Name Cell - 22% width */}
                                        <TableCell sx={{
                                            py: 2,
                                            fontWeight: 600,
                                            color: '#042560',
                                            fontSize: { xs: '0.9rem', sm: '1rem' },
                                            width: '22%',
                                            maxWidth: 0, // This forces text truncation
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap'
                                        }}>
                                            {isInlineEditing ? (
                                                <TextField
                                                    fullWidth
                                                    size="small"
                                                    value={inlineEditData[UserKeys.USER_NAME] || ''}
                                                    onChange={(e) => handleInlineDataChange(UserKeys.USER_NAME, e.target.value)}
                                                    sx={{
                                                        '& .MuiOutlinedInput-root': {
                                                            height: '40px',
                                                            fontSize: { xs: '0.9rem', sm: '1rem' },
                                                            backgroundColor: 'white',
                                                            '& fieldset': {
                                                                borderColor: 'rgba(60, 176, 67, 0.5)',
                                                                borderWidth: '2px'
                                                            },
                                                            '&:hover fieldset': {
                                                                borderColor: '#3CB043'
                                                            },
                                                            '&.Mui-focused fieldset': {
                                                                borderColor: '#3CB043',
                                                                borderWidth: '2px'
                                                            },
                                                        }
                                                    }}
                                                />
                                            ) : (
                                                <Tooltip title={user.user_name} arrow placement="top">
                                                    <span>{user.user_name}</span>
                                                </Tooltip>
                                            )}
                                        </TableCell>

                                        {/* Email Cell - 28% width */}
                                        <TableCell sx={{
                                            py: 2,
                                            color: '#374151',
                                            fontSize: { xs: '0.9rem', sm: '1rem' },
                                            width: '28%',
                                            maxWidth: 0,
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap'
                                        }}>
                                            {isInlineEditing ? (
                                                <TextField
                                                    fullWidth
                                                    size="small"
                                                    type="email"
                                                    value={inlineEditData[UserKeys.USER_EMAIL] || ''}
                                                    onChange={(e) => handleInlineDataChange(UserKeys.USER_EMAIL, e.target.value)}
                                                    sx={{
                                                        '& .MuiOutlinedInput-root': {
                                                            height: '40px',
                                                            fontSize: { xs: '0.9rem', sm: '1rem' },
                                                            backgroundColor: 'white',
                                                            '& fieldset': {
                                                                borderColor: 'rgba(60, 176, 67, 0.5)',
                                                                borderWidth: '2px'
                                                            },
                                                            '&:hover fieldset': {
                                                                borderColor: '#3CB043'
                                                            },
                                                            '&.Mui-focused fieldset': {
                                                                borderColor: '#3CB043',
                                                                borderWidth: '2px'
                                                            },
                                                        }
                                                    }}
                                                />
                                            ) : (
                                                <Tooltip title={user.email} arrow placement="top">
                                                    <span>{user.email}</span>
                                                </Tooltip>
                                            )}
                                        </TableCell>

                                        {/* Role Cell - 18% width */}
                                        <TableCell sx={{
                                            py: 2,
                                            color: '#374151',
                                            fontSize: { xs: '0.9rem', sm: '1rem' },
                                            width: '18%',
                                            maxWidth: 0,
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap'
                                        }}>
                                            {isInlineEditing ? (
                                                <Autocomplete
                                                    size="small"
                                                    options={userTypes}
                                                    getOptionLabel={(option) => option.user_role_name || option.role || ""}
                                                    value={
                                                        userTypes.find(
                                                            type =>
                                                                type.user_role_id === inlineEditData[UserKeys.USER_ROLE_ID] ||
                                                                type.id === inlineEditData[UserKeys.USER_ROLE_ID]
                                                        ) || null
                                                    }
                                                    onChange={(event, newValue) => {
                                                        handleInlineDataChange(
                                                            UserKeys.USER_ROLE_ID,
                                                            newValue ? (newValue.user_role_id || newValue.id) : ""
                                                        );
                                                    }}
                                                    renderInput={(params) => (
                                                        <TextField
                                                            {...params}
                                                            sx={{
                                                                '& .MuiOutlinedInput-root': {
                                                                    height: '40px',
                                                                    fontSize: { xs: '0.9rem', sm: '1rem' },
                                                                    backgroundColor: 'white',
                                                                    '& fieldset': {
                                                                        borderColor: 'rgba(60, 176, 67, 0.5)',
                                                                        borderWidth: '2px'
                                                                    },
                                                                    '&:hover fieldset': {
                                                                        borderColor: '#3CB043'
                                                                    },
                                                                    '&.Mui-focused fieldset': {
                                                                        borderColor: '#3CB043',
                                                                        borderWidth: '2px'
                                                                    },
                                                                }
                                                            }}
                                                        />
                                                    )}
                                                />
                                            ) : (
                                                <Tooltip title={user.user_role_name} arrow placement="top">
                                                    <span>{user.user_role_name}</span>
                                                </Tooltip>
                                            )}
                                        </TableCell>

                                        {/* Terminal Cell - 16% width */}
                                        <TableCell sx={{
                                            py: 2,
                                            color: '#374151',
                                            fontSize: { xs: '0.9rem', sm: '1rem' },
                                            width: '16%',
                                            maxWidth: 0,
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap'
                                        }}>
                                            {isInlineEditing ? (
                                                <Autocomplete
                                                    size="small"
                                                    options={userTerminals}
                                                    getOptionLabel={(option) => option.terminal_name || option.name || ""}
                                                    value={userTerminals.find(terminal =>
                                                        terminal.terminal_id === inlineEditData[UserKeys.TERMINAL_ID] ||
                                                        terminal.id === inlineEditData[UserKeys.TERMINAL_ID]
                                                    ) || null}
                                                    onChange={(event, newValue) => {
                                                        handleInlineDataChange(
                                                            UserKeys.TERMINAL_ID,
                                                            newValue ? (newValue.terminal_id || newValue.id) : ""
                                                        );
                                                    }}
                                                    renderInput={(params) => (
                                                        <TextField
                                                            {...params}
                                                            sx={{
                                                                '& .MuiOutlinedInput-root': {
                                                                    height: '40px',
                                                                    fontSize: { xs: '0.9rem', sm: '1rem' },
                                                                    backgroundColor: 'white',
                                                                    '& fieldset': {
                                                                        borderColor: 'rgba(60, 176, 67, 0.5)',
                                                                        borderWidth: '2px'
                                                                    },
                                                                    '&:hover fieldset': {
                                                                        borderColor: '#3CB043'
                                                                    },
                                                                    '&.Mui-focused fieldset': {
                                                                        borderColor: '#3CB043',
                                                                        borderWidth: '2px'
                                                                    },
                                                                }
                                                            }}
                                                        />
                                                    )}
                                                />
                                            ) : (
                                                <Tooltip title={user.user_terminal_name} arrow placement="top">
                                                    <span>{user.user_terminal_name}</span>
                                                </Tooltip>
                                            )}
                                        </TableCell>

                                        {/* Status Cell - 10% width */}
                                        <TableCell sx={{
                                            py: 2,
                                            textAlign: 'center',
                                            width: '10%'
                                        }}>
                                            {isInlineEditing ? (
                                                <Autocomplete
                                                    size="small"
                                                    options={[
                                                        { id: 1, name: 'Active' },
                                                        { id: 0, name: 'Inactive' }
                                                    ]}
                                                    getOptionLabel={(option) => option.name || ""}
                                                    value={selectedStatus}
                                                    onChange={(event, newValue) => {
                                                        handleInlineDataChange(UserKeys.USER_STATUS, newValue ? newValue.id : 1);
                                                    }}
                                                    renderInput={(params) => (
                                                        <TextField
                                                            {...params}
                                                            sx={{
                                                                '& .MuiOutlinedInput-root': {
                                                                    height: '40px',
                                                                    fontSize: { xs: '0.9rem', sm: '1rem' },
                                                                    backgroundColor: 'white',
                                                                    '& fieldset': {
                                                                        borderColor: 'rgba(60, 176, 67, 0.5)',
                                                                        borderWidth: '2px'
                                                                    },
                                                                    '&:hover fieldset': {
                                                                        borderColor: '#3CB043'
                                                                    },
                                                                    '&.Mui-focused fieldset': {
                                                                        borderColor: '#3CB043',
                                                                        borderWidth: '2px'
                                                                    },
                                                                }
                                                            }}
                                                        />
                                                    )}
                                                />
                                            ) : (
                                                <Chip
                                                    icon={user.user_status === 1 ? <Visibility fontSize="small" /> : <VisibilityOff fontSize="small" />}
                                                    label={user.user_status === 1 ? 'Active' : 'Inactive'}
                                                    size="small"
                                                    sx={{
                                                        backgroundColor: user.user_status === 1 ? 'rgba(60, 176, 67, 0.15)' : 'rgba(239, 68, 68, 0.15)',
                                                        color: user.user_status === 1 ? '#3CB043' : '#dc2626',
                                                        fontWeight: 600,
                                                        borderRadius: '10px',
                                                        fontSize: { xs: '0.75rem', sm: '0.8rem' },
                                                        height: '28px',
                                                        border: `2px solid ${user.user_status === 1 ? 'rgba(60, 176, 67, 0.3)' : 'rgba(239, 68, 68, 0.3)'}`,
                                                        maxWidth: '90px' // Prevent chip from being too wide
                                                    }}
                                                />
                                            )}
                                        </TableCell>

                                        {/* Actions Cell - 6% width */}
                                        {canUpdateUser && (
                                            <TableCell
                                                sx={{
                                                    py: 2,
                                                    textAlign: 'center',
                                                    width: '6%'
                                                }}
                                            >
                                                {isInlineEditing ? (
                                                    <Stack direction="row" spacing={1.5} justifyContent="center">
                                                        <IconButton
                                                            onClick={handleSaveInlineEdit}
                                                            size="small"
                                                            sx={{
                                                                color: 'white',
                                                                backgroundColor: '#042560',
                                                                '&:hover': {
                                                                    backgroundColor: '#031e4d',
                                                                    transform: 'scale(1.1)'
                                                                },
                                                                borderRadius: '6px',
                                                                transition: 'all 0.2s ease',
                                                                width: '32px',
                                                                height: '32px'
                                                            }}
                                                        >
                                                            <Save fontSize="small" />
                                                        </IconButton>
                                                        <IconButton
                                                            onClick={handleCancelInlineEdit}
                                                            size="small"
                                                            sx={{
                                                                color: '#ef4444',
                                                                backgroundColor: 'rgba(239, 68, 68, 0.1)',
                                                                '&:hover': {
                                                                    backgroundColor: 'rgba(239, 68, 68, 0.2)',
                                                                    transform: 'scale(1.1)'
                                                                },
                                                                borderRadius: '6px',
                                                                transition: 'all 0.2s ease',
                                                                width: '32px',
                                                                height: '32px'
                                                            }}
                                                        >
                                                            <Close fontSize="small" />
                                                        </IconButton>
                                                    </Stack>
                                                ) : (
                                                    <IconButton
                                                        onClick={() => handleStartInlineEdit(user)}
                                                        size="small"
                                                        sx={{
                                                            color: '#042560',
                                                            backgroundColor: 'rgba(4, 37, 96, 0.1)',
                                                            '&:hover': {
                                                                backgroundColor: 'rgba(4, 37, 96, 0.2)',
                                                                transform: 'scale(1.1)'
                                                            },
                                                            borderRadius: '8px',
                                                            transition: 'all 0.2s ease',
                                                            width: '36px',
                                                            height: '36px'
                                                        }}
                                                    >
                                                        <BorderColorIcon fontSize="small" />
                                                    </IconButton>
                                                )}
                                            </TableCell>
                                        )}
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>

            {/* Responsive Pagination */}
            {totalPages > 1 && (
                <Box sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    justifyContent: 'space-between',
                    alignItems: { xs: 'stretch', sm: 'center' },
                    gap: { xs: 2, sm: 0 },
                    p: { xs: 2, sm: 3 },
                    background: 'linear-gradient(135deg, #f8fafc 0%, #f0fdf4 100%)',
                    borderTop: '2px solid rgba(60, 176, 67, 0.1)'
                }}>
                    <Box sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        alignItems: { xs: 'flex-start', sm: 'center' },
                        gap: { xs: 1, sm: 3 }
                    }}>
                        <Typography variant="body2" sx={{
                            color: '#374151',
                            fontWeight: 500,
                            fontSize: { xs: '0.8rem', sm: '0.875rem' }
                        }}>
                            Showing {paginationInfo.startIndex} to {paginationInfo.endIndex} of {paginationInfo.totalItems} entries
                        </Typography>
                        {totalPages > 10 && (
                            <Box sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1.5,
                                flexWrap: 'wrap'
                            }}>
                                <Typography variant="body2" sx={{
                                    color: '#042560',
                                    fontWeight: 500,
                                    fontSize: { xs: '0.8rem', sm: '0.875rem' }
                                }}>
                                    Jump to page:
                                </Typography>
                                <TextField
                                    size="small"
                                    type="number"
                                    inputProps={{ min: 1, max: totalPages }}
                                    value={jumpToPage}
                                    onChange={(e) => handleJumpToPageChange(e.target.value)}
                                    onKeyPress={handleJumpInputKeyPress}
                                    placeholder="Page"
                                    sx={{
                                        width: '80px',
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: '8px',
                                            height: '36px',
                                            '& fieldset': { borderColor: 'rgba(4, 37, 96, 0.3)' },
                                            '&:hover fieldset': { borderColor: '#3CB043' },
                                            '&.Mui-focused fieldset': { borderColor: '#042560' },
                                        }
                                    }}
                                />
                                <Button
                                    size="small"
                                    variant="contained"
                                    onClick={handleQuickJump}
                                    sx={{
                                        backgroundColor: '#042560',
                                        color: 'white',
                                        '&:hover': { backgroundColor: '#031e4d' },
                                        minWidth: 'auto',
                                        px: 2,
                                        fontSize: '12px',
                                        textTransform: 'none',
                                        borderRadius: '8px',
                                        height: '36px'
                                    }}
                                >
                                    Go
                                </Button>
                            </Box>
                        )}
                    </Box>

                    <Stack direction="row" spacing={2} alignItems="center" sx={{
                        justifyContent: { xs: 'center', sm: 'flex-end' },
                        mt: { xs: 1, sm: 0 }
                    }}>
                        <Pagination
                            count={totalPages}
                            page={paginationInfo.currentPage}
                            onChange={(event, page) => handlePageChange(page)}
                            size="small"
                            showFirstButton
                            showLastButton
                            siblingCount={1}
                            boundaryCount={1}
                            sx={{
                                '& .MuiPaginationItem-root': {
                                    color: '#042560',
                                    borderRadius: '8px',
                                    fontWeight: 500,
                                    fontSize: { xs: '0.8rem', sm: '0.875rem' },
                                    minWidth: { xs: '32px', sm: '36px' },
                                    height: { xs: '32px', sm: '36px' },
                                    '&:hover': {
                                        backgroundColor: 'rgba(60, 176, 67, 0.1)'
                                    },
                                    '&.Mui-selected': {
                                        backgroundColor: '#042560',
                                        color: 'white',
                                        '&:hover': {
                                            backgroundColor: '#031e4d'
                                        }
                                    }
                                }
                            }}
                        />
                    </Stack>
                </Box>
            )}

            {/* No Users Found State */}
            {filteredUsers.length === 0 && (
                <Box sx={{
                    textAlign: 'center',
                    p: { xs: 4, sm: 6 },
                    color: '#6b7280',
                    backgroundColor: 'white'
                }}>
                    <Group sx={{ fontSize: 48, color: '#d1d5db', mb: 2 }} />
                    <Typography variant="h6" sx={{
                        fontWeight: 600,
                        color: '#374151',
                        mb: 1,
                        fontSize: { xs: '1.1rem', sm: '1.25rem' }
                    }}>
                        No users found
                    </Typography>
                    <Typography variant="body2" sx={{
                        color: '#6b7280',
                        fontSize: { xs: '0.8rem', sm: '0.875rem' }
                    }}>
                        No users match your search criteria. Try adjusting your search terms.
                    </Typography>
                </Box>
            )}
        </Paper>
    );

    return (
        <>
            <CBackDrop
                cOpen={isLoading}
                cHandleClose={() => { }}
                cAllowClickToClose={false}
            />
            <Box sx={{
                minHeight: '100vh',
                background: 'rgb(254, 254, 254)',
                pt: { xs: '60px', sm: '70px', md: '84px' },
                pb: 4,
                px: { xs: 1, sm: 2, md: 4, lg: 8 }
            }}>
                <Container maxWidth="xl" sx={{ px: { xs: 0, sm: 2 } }}>
                    <Box sx={{ transition: 'all 0.3s ease' }}>
                        {activeTab === 'users' && renderUsersList()}
                        {activeTab === 'permissions' && <PermissionView />}
                        {activeTab === 'data' && <DataManagementView />}
                    </Box>
                </Container>
            </Box>
        </>
    );
};

export default UserManagementView;